import { Image } from '@silvertours/common-landingpages-view';
import {
  ImageWithFocalPoint,
  MediaLegacy,
} from '@silvertours/front-legacy-shared';
import { useCallback, useState, startTransition } from 'react';
import {
  analytics,
  useContentModule,
  useSiteInfo,
} from '@silvertours/front-entities';
import { useCloseOnBackNavigation, type http } from '@silvertours/front-shared';
import { SearchForm, isOneWay } from '../SearchForm';
import { Wrapper, TrustpilotWidget } from './SearchContainer.styles';
import { SearchWizard, useSearchWizard } from '../SearchWizard';
import { matchError, useSubmitHandler } from '../SearchFormLegacy';
import { useSearchContext } from '../SearchFormLegacy/context';
import { isFormError } from '../SearchFormLegacy/context/types';

type Props = {
  /** The stage image. The `card` layout does not use it. */
  image?: MediaLegacy.ImageProps | Image;
  includeSearch?: boolean;
  isErrorPage?: boolean;
  displayMode?: 'full' | 'thin';
  isModernStage?: boolean;
  /**
   * `card` shows only the search card, without the stage image and the
   * Trustpilot widget (index page variant).
   */
  layout?: 'default' | 'card';
  headline?: string;
  buttonLabel?: string;
};

const SearchContainer = ({
  image,
  includeSearch,
  isErrorPage,
  displayMode,
  isModernStage,
  layout = 'default',
  headline,
  buttonLabel,
}: Props) => {
  const isCard = layout === 'card';
  const { featureToggles } = useSiteInfo();
  const [showWizard, setShowWizard] = useState(false);
  useCloseOnBackNavigation(showWizard, () => setShowWizard(false));

  const action = () => {
    startTransition(() => setShowWizard(true));
  };

  const { state, setError } = useSearchContext();
  const submitHandler = useSubmitHandler();
  const { getTrustpilotData } = useContentModule();
  const trustpilotProps = getTrustpilotData();

  const [loading, setLoading] = useState(false);

  const onSuccess = useCallback(() => {
    setLoading(false);
    analytics.gtm.trackSearchSubmit(isOneWay(state));
  }, [state]);

  const onError = useCallback((error: http.AxiosError) => {
    setLoading(false);
    const { type: kind, message } = matchError(error);

    if (isFormError(kind)) {
      setError({ kind, message });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { scenes } = useSearchWizard();

  if (showWizard) {
    return (
      <SearchWizard
        loading={loading}
        scenes={scenes}
        onClose={() => setShowWizard(false)}
        onConfirm={() => {
          setLoading(true);
          void submitHandler(state, '', onSuccess, onError);
        }}
      />
    );
  }

  return (
    <Wrapper
      isErrorPage={isErrorPage}
      $isThin={displayMode === 'thin'}
      $isModernStage={isModernStage}
      $isCard={isCard}
    >
      {!isCard &&
        !!image &&
        !isErrorPage &&
        !isModernStage &&
        displayMode !== 'thin' && (
          <ImageWithFocalPoint {...image} id="stage-image-mobile" priority />
        )}
      {includeSearch && (
        <SearchForm
          searchAction={action}
          displayMode={displayMode}
          isModernStage={isModernStage}
          layout={layout}
          headline={headline}
          buttonLabel={buttonLabel}
        />
      )}
      {!isCard &&
        featureToggles.trustElementOnStageImage &&
        trustpilotProps && (
          <TrustpilotWidget
            {...trustpilotProps}
            $isModernStage={isModernStage}
          />
        )}
    </Wrapper>
  );
};

export { SearchContainer };
